<i18n lang="yaml">
en:
  meta:
    title: Recreational Vehicles for Sale (New & Used) | RVezy
    description: Browse a variety of new and used RVs for sale near you on RVezy—shop Class A, B, C, and towable RVs from all the top RV manufacturers.
  heading: RVs for sale

fr:
  meta:
    title: Véhicules de loisirs à vendre (neufs et d'occasion) | RVezy
    description: Découvrez une variété de véhicules récréatifs neufs et d'occasion à vendre près de chez vous sur RVezy - magasinez des véhicules récréatifs de classe A, B, C et remorquables de tous les principaux fabricants de véhicules récréatifs.
  heading: VR à vendre
</i18n>

<template>
  <AppSearchResults
    v-if="searchResults"
    :title="t('heading')"
    :rvs="searchResults.results"
    :total-results="searchResults.totalResults"
    page-source="search"
    :cta="Ctas.SearchResults"
    :page-size="pageSize"
    :current-page="pagination.value.currentPage"
    :search-parameters="sp.searchParameters"
  />
  <div v-show="pending" class="loading">
    <CommonSpinner />
  </div>
</template>

<script lang="ts" setup>
import { Ctas } from '~/enums/ctas'
import { captureError } from '~/lib/logger'
import { extractSearchQuery } from '~/lib/query'
import { queryStringToLocation, queryStringToSearchFilters, searchParametersToApiRequest } from '~/lib/search'
import { AppProviderKeys } from '~/providers'
import type { CountryCodes, RegionCodes } from '~/types/api'
import type { IPPagination } from '~/types/provider/pagination'
import type { IPSearchParameters } from '~/types/provider/search-parameters'

definePageMeta({
  layout: 'search'
})

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: '/search',
    fr: '/recherche'
  }
})

const route = useRoute()
const { t } = useI18n()
const $config = useRuntimeConfig()
const { GetAutoCompleteService, GetGeocoderService } = useGooglePlaceLib()
const { getMarketplaceApiClient } = useApiClients()

useHead({
  title: t('meta.title'),
  meta: [
    { name: 'description', content: t('meta.description') },
    { name: 'robots', content: 'noindex' }
  ]
})

const geolocation = useGeolocation()
const sp = inject<IPSearchParameters>(AppProviderKeys.SearchParameters) as IPSearchParameters
const pagination = inject<IPPagination>(AppProviderKeys.Pagination) as IPPagination

const searchParametersChanged = ref(0)

const pageSize = 30

onMounted(() => {
  //This is a workaround for this bug on NUXT navigation: https://github.com/nuxt/nuxt/issues/25069
  //When the user go to a listing and then go back to the search page the query string is not updated in time to initialize the search parameters
  //So, when this happens we need to reload the page
  if (route.query) {
    const query = extractSearchQuery(route.query)
    const queryFilters = queryStringToSearchFilters(query)

    Object.keys(queryFilters).forEach((key) => {
      if (JSON.stringify(queryFilters[key]) !== JSON.stringify(sp.searchParameters.filters[key])) {
        window.location.reload()
      }
    })
  }
})

const {
  pending,
  data: searchResults,
  error: fetchError
} = await useAsyncData(
  'rvs',
  async () => {
    const requestPayload = searchParametersToApiRequest(sp.searchParameters)
    const autoCompleteService = await GetAutoCompleteService()
    const geocodeService = await GetGeocoderService()

    const query = extractSearchQuery(route.query)
    if (!sp.searchParameters.location)
      sp.searchParameters.location = await queryStringToLocation(query, autoCompleteService, geocodeService)

    if (!sp.searchParameters.location?.city) {
      requestPayload.RegionCode = sp.searchParameters.location?.region?.short_name as RegionCodes
      requestPayload.CountryCode = sp.searchParameters.location?.country?.short_name as CountryCodes
    } else {
      requestPayload.Latitude =
        sp.searchParameters.location?.center?.lat ?? geolocation?.latitude ?? $config.public.app.defaultLatitude
      requestPayload.Longitude =
        sp.searchParameters.location?.center?.lng ?? geolocation?.longitude ?? $config.public.app.defaultLongitude
    }

    requestPayload.Page = pagination.value.currentPage
    requestPayload.PageSize = pageSize

    // Used to make easier to find dealers
    if (route.query.DealerId) requestPayload.DealerId = route.query.DealerId as string
    if (route.query.LocationId) requestPayload.LocationId = route.query.LocationId as string

    const { data: searchResults } = await getMarketplaceApiClient().GET('/Rv/Search', {
      params: { query: requestPayload }
    })

    return searchResults
  },

  {
    watch: [searchParametersChanged]
  }
)

if (fetchError.value) captureError(toRaw(fetchError.value))

watch(
  () => pagination.value.currentPage,
  () => {
    searchParametersChanged.value++
  }
)

watch(
  () => sp.searchParameters,
  () => {
    searchParametersChanged.value++
    pagination.updateCurrentPage(1)
  },
  { deep: true }
)
</script>
